import React, { useState, createContext, useEffect, useContext, useMemo, useRef, Suspense } from "react";
import { Navigate, Link, createBrowserRouter, RouterProvider, Outlet, ScrollRestoration, useNavigate, useLocation, useNavigationType } from 'react-router-dom';
// import App from './App'; // deprecated
import './App.css';
import { useMantineColorScheme } from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";

import { useGoogleLogin, GoogleLogin } from '@react-oauth/google';
import axios from 'axios';

import Notifications from './components/Notifications';
import AddEvent from './components/AddEvent';
import PersonalArtEvents from './components/PersonalArtEvents';

import axiosInstance, { baseURL } from './axiosInstance.js';
import MyUserContext from './myUserContext.js';
import PageHeaderContext from './pageHeaderContext.js';
import FooterSuppressContext from './footerSuppressContext.js';
import * as oauth from './oauth.js';

import TelegramLoginButton from './components/TelegramLoginButton';
// Rating helpers shared with components/ImageScroller so LoggedOutDiscover
// and the Similar section use the same priority / normalization / dedup.
import { getTopRatings as getTopDiscoverRatings, RatingPillsStack } from './components/ratingPills';
import ReactGA from 'react-ga4'; // Import Google Analytics
import { IconHome, IconSearch } from "@tabler/icons-react";
import {
  AppShell,
  Avatar,
  Box,
  Button,
  Container,
  Divider,
  Group,
  Indicator,
  Paper,
  Stack,
  Text,
  Title,
  UnstyledButton,
  PinInput,
  TextInput
} from "@mantine/core";

// Lazy-loaded route components (not needed on initial page load)
const ArtEvent = React.lazy(() => import('./components/ArtEvent'));
const UserPage = React.lazy(() => import('./components/UserPage'));
const Users = React.lazy(() => import('./components/Users'));
const Settings = React.lazy(() => import('./components/Settings'));
const ArtPage = React.lazy(() => import('./components/ArtPage'));
const ArtReviewPage = React.lazy(() => import('./components/ArtReviewPage'));
const ReviewByIdPage = React.lazy(() => import('./components/ReviewByIdPage'));
const LogbookHistory = React.lazy(() => import('./components/LogbookHistory'));
const LogbookPage = React.lazy(() => import('./components/LogbookPage'));
const RecalcUser = React.lazy(() => import('./components/RecalcUser'));
const TgOauthCallback = React.lazy(() => import('./components/TgOauthCallback'));
const CreateArt = React.lazy(() => import('./components/CreateArt'));
const CreateArtProposal = React.lazy(() => import('./components/CreateArtProposal'));
const ArtProposals = React.lazy(() => import('./components/ArtProposals'));
const AdminLetterboxdFailures = React.lazy(() => import('./components/AdminLetterboxdFailures'));
const AdminSearchNotFoundHints = React.lazy(() => import('./components/AdminSearchNotFoundHints'));
const AdminDirectChatAllowedUsers = React.lazy(() => import('./components/AdminDirectChatAllowedUsers'));
const AdminWatchLinkAllowedUsers = React.lazy(() => import('./components/AdminWatchLinkAllowedUsers'));
const PlatformRecommendations = React.lazy(() => import('./components/PlatformRecommendations'));
const TopTodos = React.lazy(() => import('./components/TopTodos'));
const NewUser = React.lazy(() => import('./components/NewUser'));
const DirectChatPage = React.lazy(() => import('./components/DirectChatPage'));
const GroupChatPage = React.lazy(() => import('./components/GroupChatPage'));
const MyChats = React.lazy(() => import('./components/MyChats'));

const MainPageUIContext = createContext(null);

const Layout = ({loggedIn}) => {
  const {
    selectedTab,
    setSelectedTab,
    repeatedEnter,
    setRepeatedEnter,
    myUserSettings,
    myUserInfo,
    unreadCount,
    isRussianSpeaking
  } = useContext(MainPageUIContext);

  const navigate = useNavigate();
  const location = useLocation();
  const navType = useNavigationType();
  // Mantine 'sm' breakpoint = 48em ≈ 768px, matches the hiddenFrom="sm"
  // used on the footer. Used only to reposition the fixed back button.
  const isDesktop = useMediaQuery('(min-width: 48em)');
  // Pages that render their own PageHeader (with their own back button
  // inside). Primary signal is the pathname — PageHeaderContext would
  // only flip AFTER the lazy page chunk loads and PageHeader mounts,
  // so the global back button would briefly paint during the suspense
  // and then swap out. Context is still OR'd as a fallback for future
  // non-lazy pages.
  const { active: contextPageHeaderActive } = useContext(PageHeaderContext);
  const { active: footerSuppressActive } = useContext(FooterSuppressContext);
  // Chat routes always hide the mobile floating footer so its 60px pill
  // doesn't bury the message input. The /artEvents/:id overlay signals
  // the same thing through FooterSuppressContext when it opens.
  const inChatRoute =
    /^\/directChats\/[^/]+\/?$/.test(location.pathname) ||
    /^\/groupChats\/[^/]+\/?$/.test(location.pathname);
  const suppressFooter = inChatRoute || footerSuppressActive;
  const pageHeaderActive =
    contextPageHeaderActive ||
    /^\/arts\/[^/]+\/?$/.test(location.pathname) ||
    /^\/artEvents\/[^/]+\/?$/.test(location.pathname) ||
    /^\/users\/[^/]+\/likes\/?$/.test(location.pathname) ||
    /^\/users\/[^/]+\/recommendations\/?$/.test(location.pathname) ||
    /^\/users\/?$/.test(location.pathname) ||
    /^\/addEvent\/?$/.test(location.pathname) ||
    /^\/chats\/?$/.test(location.pathname) ||
    /^\/directChats\/[^/]+\/?$/.test(location.pathname) ||
    /^\/groupChats\/[^/]+\/?$/.test(location.pathname);

  // Instagram pattern: footer tab bar visible only on root tabs.
  const myIdForTabCheck = myUserInfo?.myUserId;
  const isRootTab =
    location.pathname === '/notifications' ||
    location.pathname === '/' ||
    location.pathname === '/addEvent' ||
    (!!myIdForTabCheck && location.pathname === `/users/${myIdForTabCheck}/likes`);

  // Tab-scoped nav counter: bumps on every URL change (pathname or search),
  // survives refresh via sessionStorage. > 0 means user has navigated at
  // least once in this tab, so there's an in-app entry to go back to.
  const [navCount, setNavCount] = useState(() => {
    try { return parseInt(sessionStorage.getItem('_navCount') || '0', 10); }
    catch { return 0; }
  });
  const isFirstNavFire = useRef(true);
  const navResetPending = useRef(false);
  useEffect(() => {
    if (isFirstNavFire.current) { isFirstNavFire.current = false; return; }
    if (navResetPending.current) {
      navResetPending.current = false;
      setNavCount(0);
      try { sessionStorage.setItem('_navCount', '0'); } catch {}
      return;
    }
    setNavCount((prev) => {
      // PUSH goes deeper, POP unwinds, REPLACE leaves the counter alone.
      let next = prev;
      if (navType === 'PUSH') next = prev + 1;
      else if (navType === 'POP') next = Math.max(0, prev - 1);
      try { sessionStorage.setItem('_navCount', String(next)); } catch {}
      return next;
    });
  }, [location.pathname, location.search, navType]);

  // Detect virtual keyboard open (Android resizes visualViewport, not window)
  const [keyboardOpen, setKeyboardOpen] = useState(false);
  useEffect(() => {
    const isMobile = /Mobi|Android/i.test(navigator.userAgent);
    const vv = window.visualViewport;
    if (!isMobile || !vv) return;
    const onResize = () => {
      const open = vv.height < window.innerHeight * 0.75;
      setKeyboardOpen(open);
      document.documentElement.style.setProperty('--artevent-pad-bottom', open ? '0px' : '');
      // Height of the on-screen keyboard (visual viewport is shrunk by
      // the keyboard; layout viewport is not). Fixed-bottom layouts
      // read this so their bottom sits above the keyboard instead of
      // behind it.
      const kbOffset = Math.max(0, window.innerHeight - vv.height - vv.offsetTop);
      document.documentElement.style.setProperty('--keyboard-offset', kbOffset + 'px');
    };
    onResize();
    vv.addEventListener('resize', onResize);
    vv.addEventListener('scroll', onResize);
    return () => {
      vv.removeEventListener('resize', onResize);
      vv.removeEventListener('scroll', onResize);
    };
  }, []);

  const isRussian = myUserSettings?.lang === "ru" || isRussianSpeaking;

  const avatarUrl = myUserSettings?.avatarUrl || "avatar_neutral.jpeg";
  const avatarSrc = avatarUrl.startsWith("http")
    ? `${baseURL}/avatarProxy?url=${encodeURIComponent(avatarUrl)}`
    : avatarUrl;

  const handleNavClick = (tabIndex) => {
    // Main navbar click starts a fresh section: clear the back-button counter.
    navResetPending.current = true;
    setRepeatedEnter(prev => prev + 1);
    setSelectedTab(tabIndex);
  };

  const notificationIcon = (
    <Indicator
      size={16}
      color="indigo"
      disabled={!unreadCount}
      label={unreadCount ? Math.min(unreadCount, 99) : undefined}
    >
      <IconHome size={22} stroke={1.5} />
    </Indicator>
  );

  const navItems = [
    {
      key: 0,
      label: isRussian ? "Лента" : "Home",
      to: loggedIn ? "/notifications" : "/",
      icon: notificationIcon
    },
    {
      key: 1,
      label: isRussian ? "Поиск" : "Search",
      to: "/addEvent",
      icon: <IconSearch size={22} stroke={1.5} />
    },
    {
      key: 4,
      label: isRussian ? "Моя библиотека" : "My library",
      to: loggedIn ? `/users/${myUserInfo?.myUserId || ""}/likes` : "/",
      icon: (
        <Avatar
          src={avatarSrc}
          radius="xl"
          size={29}
        />
      )
    }
  ];

  const navButtonStyle = (isActive) => ({
    padding: '6px 12px',
    borderRadius: 999,
    transition: 'background-color 120ms ease',
    backgroundColor: isActive ? 'var(--mantine-primary-color-light)' : 'transparent',
  });

  const navIconStyle = (isActive) => ({
    width: 40,
    height: 40,
    borderRadius: 999,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: isActive ? 'var(--mantine-primary-color-light)' : 'var(--mantine-color-default)',
  });

  const NavButton = ({ item, layout }) => {
    const isActive = selectedTab === item.key;
    const labelSize = layout === "footer" ? "xs" : "sm";
    const labelWeight = isActive ? 600 : 500;
    return (
      <UnstyledButton
        component={Link}
        to={item.to}
        onClick={(e) => {
          // Already on this tab? Keep the current URL (including query
          // params like ?tags=…) instead of re-navigating and dropping them.
          if (location.pathname === item.to) {
            e.preventDefault();
            // Home tab on /notifications: tap Home again to soft-refresh the
            // feed (Facebook-style: keep the list, push it down with a
            // spinner, swap in fresh items).
            if (item.key === 0 && location.pathname === "/notifications") {
              window.dispatchEvent(new CustomEvent("notifications:refresh"));
              return;
            }
          }
          handleNavClick(item.key);
        }}
        style={navButtonStyle(isActive)}
      >
        {layout === "footer" ? (
          <Stack gap={4} align="center">
            <Box style={navIconStyle(isActive)}>{item.icon}</Box>
          </Stack>
        ) : (
          <Group gap="sm" wrap="nowrap">
            <Box style={navIconStyle(isActive)}>{item.icon}</Box>
            <Text size={labelSize} fw={labelWeight} c={isActive ? undefined : "dimmed"}>
              {item.label}
            </Text>
          </Group>
        )}
      </UnstyledButton>
    );
  };

  const shellNavStyle = {
    backgroundColor: 'var(--mantine-color-body)',
    backdropFilter: 'blur(16px)',
  };

  const triggerFireworks = () => {
    const canvas = document.createElement('canvas');
    canvas.style.cssText = 'position:fixed;top:0;left:0;width:100%;height:100%;pointer-events:none;z-index:9999';
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;
    document.body.appendChild(canvas);
    const ctx = canvas.getContext('2d');
    const particles = [];
    const colors = ['#ff595e','#ffca3a','#8ac926','#1982c4','#6a4c93','#ff6b6b','#feca57','#48dbfb'];
    for (let i = 0; i < 5; i++) {
      const x = Math.random() * canvas.width;
      const y = canvas.height * 0.2 + Math.random() * canvas.height * 0.3;
      for (let j = 0; j < 40; j++) {
        const angle = Math.random() * Math.PI * 2;
        const speed = Math.random() * 5 + 2;
        particles.push({ x, y, vx: Math.cos(angle) * speed, vy: Math.sin(angle) * speed, life: 1, color: colors[Math.floor(Math.random() * colors.length)], size: Math.random() * 3 + 1 });
      }
    }
    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      let alive = false;
      particles.forEach(p => {
        p.x += p.vx; p.y += p.vy; p.vy += 0.1; p.life -= 0.015;
        if (p.life > 0) {
          alive = true;
          ctx.globalAlpha = p.life;
          ctx.fillStyle = p.color;
          ctx.beginPath();
          ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
          ctx.fill();
        }
      });
      if (alive) requestAnimationFrame(animate);
      else document.body.removeChild(canvas);
    };
    requestAnimationFrame(animate);
  };

  return (
    <AppShell
      className="app-shell"
      padding={{ base: 0, sm: "md" }}
      header={{ height: 72 }}
      footer={{ height: { base: (keyboardOpen || suppressFooter) ? 0 : "calc(60px + env(safe-area-inset-bottom))", sm: 0 } }}
    >
      <AppShell.Header visibleFrom="sm" style={{ ...shellNavStyle, borderBottom: '1px solid var(--mantine-color-default-border)' }}>
        <Container size="sm" h="100%">
          <Group h="100%" justify="space-between" wrap="nowrap">
            <Group gap="sm" wrap="nowrap" onClick={() => { const target = loggedIn ? '/notifications' : '/'; if (location.pathname === target) { triggerFireworks(); handleNavClick(0); return; } handleNavClick(0); navigate(target); }} style={{ cursor: 'pointer', userSelect: 'none' }}>
              <img src="/logo_wowcontent.jpg" alt="WowContent" style={{ height: 32, borderRadius: 6 }} />
              <Text size="sm" c="dimmed" style={{ whiteSpace: 'nowrap' }}>
                {isRussian ? "Фильмы, книги, игры" : "Movies, books, games"}
              </Text>
            </Group>
            <Group gap="xs" wrap="nowrap">
              {loggedIn ? (
                navItems.map((item) => (
                  <NavButton key={item.key} item={item} layout="header" />
                ))
              ) : (
                // Logged-out: hide the "Home / Search / My library" chrome
                // that only makes sense to a signed-in user ("My library" of
                // what?). Hand the viewport to the content; offer one clear
                // Sign-in button on the right.
                <Button
                  size="xs"
                  variant="light"
                  color="indigo"
                  onClick={() => { if (location.pathname !== '/') navigate('/'); const el = document.querySelector('[data-login-form]'); if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' }); }}
                >
                  {isRussian ? "Войти" : "Sign in"}
                </Button>
              )}
            </Group>
          </Group>
        </Container>
      </AppShell.Header>

      {loggedIn && !keyboardOpen && !suppressFooter && <div className="block sm:hidden" style={{
        // Instagram iOS–style floating glass pill: plain fixed div so
        // Mantine's AppShell.Footer CSS can't override our bottom
        // offset (that was pinning the bar flush to the screen bottom
        // regardless of the inline `bottom` we set).
        position: 'fixed',
        bottom: 'calc(env(safe-area-inset-bottom, 0px) + 14px)',
        left: 12,
        right: 12,
        height: 60,
        borderRadius: 999,
        background: 'color-mix(in srgb, var(--mantine-color-body) 60%, transparent)',
        backdropFilter: 'blur(20px) saturate(180%)',
        WebkitBackdropFilter: 'blur(20px) saturate(180%)',
        boxShadow: '0 8px 24px rgba(0,0,0,0.25)',
        overflow: 'hidden',
        zIndex: 200,
      }}>
        <Container size="lg" h="100%">
          <Group h="100%" justify="space-around">
            {navItems.map((item) => (
              <NavButton key={item.key} item={item} layout="footer" />
            ))}
          </Group>
        </Container>
      </div>}

      <AppShell.Main
        pt={{ base: "xs", sm: 80 }}
        pb={{ base: (keyboardOpen || suppressFooter) ? 0 : "calc(60px + env(safe-area-inset-bottom) + 26px)", sm: "lg" }}
        style={(keyboardOpen || suppressFooter) ? { '--app-shell-footer-offset': '0px', paddingBottom: 0 } : undefined}
      >
        <Container size="lg" px={{ base: "xs", sm: "sm" }} py={{ base: 0, sm: "lg" }}>
          <Suspense fallback={<div />}>
            <Outlet />
          </Suspense>
        </Container>
      </AppShell.Main>
      {/* React Router v6 scroll restoration. Default getKey = location
          .key — a unique per-navigation key. Each distinct visit (even
          to the same URL) gets its own saved slot, so A → B → back-to-A
          restores exactly the scrollY the user had when leaving THIS
          visit of A, not whatever an earlier visit saved. Was keyed by
          pathname which caused cross-visit interference. */}
      <ScrollRestoration />
      {navCount > 0 && !isDesktop && !pageHeaderActive && !isRootTab && (
        <button
          // key by pathname so React re-mounts the button on each
          // navigation; the CSS animation below then slides it in from
          // the left, so it feels like it enters WITH the new page
          // instead of being nailed to the viewport.
          key={location.pathname}
          type="button"
          onClick={() => navigate(-1)}
          aria-label="Back"
          title="Back"
          className="back-button-enter"
          style={{
            position: 'fixed',
            // Same vertical position across every page. Desktop tuned so
            // the button aligns with the /artEvents/{id} thumb row
            // (--artevent-pad-top = 72 + mt-1 = 76); mobile matches
            // --artevent-pad-top = env + 8.
            top: isDesktop
              ? 'calc(72px + 4px)'
              : 'calc(env(safe-area-inset-top, 0px) + 8px)',
            left: isDesktop
              ? 'calc(100vw / 4)'
              : 'calc(env(safe-area-inset-left, 0px) + 12px)',
            width: 36,
            height: 36,
            padding: 0,
            borderRadius: 999,
            border: '1px solid var(--mantine-color-default-border)',
            background: 'var(--mantine-color-default)',
            color: 'var(--mantine-color-text)',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            zIndex: 300,
          }}
        >
          <span style={{ fontSize: 18, lineHeight: 1 }}>←</span>
        </button>
      )}
    </AppShell>
  );
};

// Hide items from the hero whose rating doesn't clear a quality bar.
// Trending ≠ good; a 1.2/10 release at position #1 is anti-marketing
// for a stranger landing on the site. Also drop items that have no
// rating at all — a landing hero of unrated things reads as a graveyard.
const QUALITY_FLOOR = 6.0;
const passesQualityFloor = (a) => {
  const top = getTopDiscoverRatings(a, 1);
  return top.length > 0 && top[0].normalized >= QUALITY_FLOOR;
};
const formatDiscoverRating = (r) => (r ? r.normalized.toFixed(1) : null);

// Three horizontal poster strips (movies / books / games) rendered at
// the top of the logged-out home. The whole point: a first-time visitor
// should see CONTENT above the fold, not a sign-in form in an empty
// field. Scrolls freely; each poster links straight to its /arts/<id>
// page which is itself public.
const LoggedOutDiscover = ({ isRussianSpeaking }) => {
  const [stripsByKey, setStripsByKey] = useState({ movies: null, books: null, games: null });

  useEffect(() => {
    const categories = [
      { key: 'movies', endpoint: '/trendingMovies' },
      { key: 'books', endpoint: '/trendingBooks' },
      { key: 'games', endpoint: '/trendingGames' },
    ];
    for (const { key, endpoint } of categories) {
      axiosInstance.get(endpoint)
        .then((res) => {
          const raw = Array.isArray(res?.data?.arts) ? res.data.arts : [];
          // Preserve the server's trending order (same as what logged-in
          // users see on hot-arts pages). Only quality-filter to keep
          // obvious anti-marketing items out of the stranger-facing
          // hero; fall back to raw if the filter leaves too few.
          const filtered = raw.filter(passesQualityFloor);
          const final = filtered.length >= 6 ? filtered : raw;
          setStripsByKey((prev) => ({ ...prev, [key]: final.slice(0, 18) }));
        })
        .catch(() => {
          setStripsByKey((prev) => ({ ...prev, [key]: [] }));
        });
    }
  }, []);

  // Cross-media color accent — same recipe as the library grid: films
  // orange, books grape, games teal. Doubles the signal (section label
  // + each poster's category) so the eye picks category at a glance.
  const label = (key) => {
    if (isRussianSpeaking) {
      return { movies: 'Фильмы', books: 'Книги', games: 'Игры' }[key];
    }
    return { movies: 'Films', books: 'Books', games: 'Games' }[key];
  };
  const dotColor = (key) => ({
    movies: 'var(--mantine-color-orange-filled)',
    books: 'var(--mantine-color-grape-filled)',
    games: 'var(--mantine-color-teal-filled)',
  }[key]);

  return (
    <Stack gap="md">
      {['movies', 'books', 'games'].map((key) => {
        const arts = stripsByKey[key];
        return (
          <div key={key}>
            <div className="flex items-center gap-2 mb-2 px-2">
              <span
                className="inline-block rounded-full"
                style={{ width: 8, height: 8, background: dotColor(key), flexShrink: 0 }}
              />
              <Text size="md" fw={700} style={{ color: 'var(--mantine-color-text)' }}>
                {label(key)}
              </Text>
            </div>
            <div
              className="flex gap-3 overflow-x-auto no-scrollbar pb-2 px-xs"
              style={{ scrollbarWidth: 'none' }}
            >
              {arts === null && (
                // Skeleton row — 10 placeholder tiles so the layout doesn't
                // jump when the fetch resolves.
                Array.from({ length: 10 }).map((_, i) => (
                  <div
                    key={i}
                    className="flex-shrink-0 rounded-lg"
                    style={{
                      width: 110,
                      height: 165,
                      background: 'var(--mantine-color-default)',
                      opacity: 0.6,
                    }}
                  />
                ))
              )}
              {arts && arts.length === 0 && (
                <Text size="xs" c="dimmed" px="xs">
                  {isRussianSpeaking ? 'Пока пусто' : 'Nothing yet'}
                </Text>
              )}
              {arts && arts.map((a) => {
                const displayName = (isRussianSpeaking && a.ruName) ? a.ruName : (a.name || '');
                return (
                  <Link
                    key={a.id}
                    to={`/arts/${a.id}`}
                    className="flex-shrink-0 block group"
                    title={a.year ? `${displayName} (${a.year})` : displayName}
                    style={{ width: 110 }}
                  >
                    <div
                      className="rounded-lg overflow-hidden shadow-sm transition-transform duration-150 group-hover:-translate-y-0.5 group-hover:shadow-md"
                      style={{ position: 'relative', width: 110, height: 165, background: 'var(--mantine-color-default)' }}
                    >
                      {a.thumbnailUrl && (
                        <img
                          src={a.thumbnailUrl}
                          alt=""
                          loading="lazy"
                          style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                        />
                      )}
                      <RatingPillsStack art={a} max={3} />
                    </div>
                    <Text size="xs" lineClamp={1} mt={4} style={{ color: 'var(--mantine-color-text)' }}>
                      {a.year ? `${displayName} · ${a.year}` : displayName}
                    </Text>
                  </Link>
                );
              })}
            </div>
          </div>
        );
      })}
    </Stack>
  );
};

const LoggedOutHome = () => {
  const {
    isRussianSpeaking,
    emailLogin,
    emailLoginCode,
    emailLoginMessage,
    emailLoginError,
    loginCodeRequested,
    updateMyUserInfo,
    handleLoginIdentifierChange,
    setEmailLoginCode,
    handleSendLoginCode,
    handleLoginWithCode
  } = useContext(MainPageUIContext);

  const handleCodeChange = (val) => {
    setEmailLoginCode(val);
    if (val.length === 5) {
      handleLoginWithCode(val);
    }
  };

  const pitchLine = isRussianSpeaking
    ? "Отмечайте фильмы, книги и игры. Сохраняйте планы. Рекомендуйте друзьям."
    : "Track films, books and games. Save plans. Tell your friends what's good.";

  return (
    <Container size="lg" py={{ base: "sm", sm: "md" }}>
      <Stack gap="lg">
        {/* Compact brand strip — one line pitch, no bordered Paper. The
            content IS the pitch; this is a header, not a billboard. */}
        <Group gap="sm" align="center" justify="center" wrap="nowrap" style={{ padding: '4px 8px' }}>
          <img src="/logo_wowcontent.jpg" alt="WowContent" style={{ height: 24, borderRadius: 5 }} />
          <Title order={4} style={{ fontWeight: 600 }}>WowContent</Title>
          <Text size="xs" c="dimmed" visibleFrom="sm" style={{ marginLeft: 8 }}>
            — {pitchLine}
          </Text>
        </Group>
        {/* Content wall — movies / books / games strips. This is the hero. */}
        <LoggedOutDiscover isRussianSpeaking={isRussianSpeaking} />

        {/* Compact inline login — no bordered Paper panel. The top-right
            'Войти' button is the primary path; this is a secondary
            scroll-target for anyone who prefers to see the form in-page.
            Shrunk from a full card to a one-line form + optional PIN /
            Google row that only appears after action. */}
        <div data-login-form className="mt-6 px-2" style={{ borderTop: '1px solid var(--mantine-color-default-border)', paddingTop: 20 }}>
          <Stack gap="md" align="center">
            {/* Primary: email / username login by one-time code. */}
            <Stack gap="sm" align="center" w="100%">
              <Text size="sm" fw={600}>
                {isRussianSpeaking ? "Войти / зарегистрироваться по коду" : "Sign in / sign up with code"}
              </Text>
              <Group gap="sm" justify="center" wrap="wrap">
                <TextInput
                  value={emailLogin}
                  onChange={handleLoginIdentifierChange}
                  placeholder={isRussianSpeaking ? "Имя пользователя или email" : "Username or email"}
                  radius="md"
                  w={240}
                  size="sm"
                />
                <Button
                  onClick={handleSendLoginCode}
                  variant="filled"
                  color="indigo"
                  size="sm"
                >
                  {loginCodeRequested
                    ? isRussianSpeaking ? "Отправить снова" : "Send again"
                    : isRussianSpeaking ? "Получить код" : "Get code"}
                </Button>
              </Group>
              {emailLoginMessage && !emailLoginError && (
                <Text size="sm" c="blue" ta="center">{emailLoginMessage}</Text>
              )}
              {loginCodeRequested && (
                <Group gap="sm" justify="center" wrap="wrap">
                  <PinInput
                    length={5}
                    value={emailLoginCode}
                    onChange={handleCodeChange}
                    size="sm"
                    type="number"
                    inputMode="numeric"
                  />
                  <Button
                    onClick={() => handleLoginWithCode()}
                    variant="light"
                    color="indigo"
                    size="sm"
                  >
                    {isRussianSpeaking ? "Войти" : "Log in"}
                  </Button>
                </Group>
              )}
              {emailLoginError && (
                <Text size="sm" c="red" ta="center">{emailLoginError}</Text>
              )}
            </Stack>


            {/* Secondary: third-party OAuth options. */}
            <Stack gap="lg" align="center">
              {!window.location.hostname.endsWith('.ru') && (
                <>
                  <Divider size="xs" w="40%" mt="xs" label={isRussianSpeaking ? "или" : "or"} labelPosition="center" />
                  <Box>
                    <GoogleLogin
                      onSuccess={(credentialResponse) => {
                        oauth.ProvideCredentialToBackend({
                          credential: credentialResponse.credential,
                          onJWTObtained: () => {
                            updateMyUserInfo();
                            window.location.href = "/";
                          }
                        });
                      }}
                      onError={() => {
                        console.log('Login Failed');
                      }}
                    />
                  </Box>
                </>
              )}
              {false && !window.location.hostname.endsWith('.ru') && (
                <>
                  <Divider size="xs" w="40%" label={isRussianSpeaking ? "или" : "or"} labelPosition="center" />
                  <Box>
                    <TelegramLoginButton isRussian={isRussianSpeaking} />
                  </Box>
                </>
              )}
            </Stack>
          </Stack>
        </div>
      </Stack>
    </Container>
  );
};

const PersonalArtEventsWithContext = ({ eventType, userId }) => {
  const { repeatedEnter } = useContext(MainPageUIContext);
  return (
    <PersonalArtEvents repeatedEnter={repeatedEnter} eventType={eventType} userId={userId} />
  );
};

const AddEventWithContext = () => {
  const { repeatedEnter } = useContext(MainPageUIContext);
  return <AddEvent repeatedEnter={repeatedEnter} />;
};

const SettingsWithContext = () => {
  const { updateMyUserSettings, handleLogout } = useContext(MainPageUIContext);
  return <Settings onSettingsChange={updateMyUserSettings} onLogout={handleLogout} />;
};

const NotificationsWithContext = () => {
  const { setUnreadCount } = useContext(MainPageUIContext);
  return <Notifications unreadCountCallback={setUnreadCount} />;
};

function MainPage() {
  const { setColorScheme } = useMantineColorScheme();
  const [isRussianSpeaking, setIsRussianSpeaking] = useState(() => {
    if (window.location.hostname.endsWith('.ru')) return true;
    const lang = navigator.language || navigator.userLanguage || '';
    return lang.startsWith('ru');
  });

  const [loginState, setLoginState] = useState(null); // also, loggedIn and loggedOut
  const [oauthLoginURL, setOauthLoginURL] = useState(null);
  const [emailLogin, setEmailLogin] = useState("");
  const [emailLoginCode, setEmailLoginCode] = useState("");
  const [emailLoginMessage, setEmailLoginMessage] = useState("");
  const [emailLoginError, setEmailLoginError] = useState("");
  const [loginCodeRequested, setLoginCodeRequested] = useState(false);

  const [myUserInfo, setMyUserInfo] = useState(null);
  const [myUserSettings, setMyUserSettings] = useState(null);
  // FEATURE: global AI badge on art thumbnails. Safe to remove together with
  // the matching block in ArtThumbnail.js and the /user/aiRecommendedArtIds backend route.
  const [aiRecommendedArtIds, setAiRecommendedArtIds] = useState(() => new Set());

  // const [jwtObtained, setJWTObtained] = useState(false); // TODO: better use one more loginState (oauthRedirecting?)

  const [unreadCount, setUnreadCount] = useState(null);

  const [selectedTab, setSelectedTab] = useState(0); // profile page is 0, increments to the right
  const [repeatedEnter, setRepeatedEnter] = useState(0);

  const updateMyUserInfo = () => {
    axiosInstance
    .get(`/checkLoggedIn`)
    .then((response) => {     
      setMyUserInfo(response.data);
      setLoginState('loggedIn');
    })
    .catch(error => {
      if (error.response.status === 401) {
        setLoginState('loggedOut');
        // setJWTObtained(false);
      } else {
        // console.error('Error checking login status:', error);  
      }
    });
  }

  const fetchMyUserSettings = () => {
    axiosInstance
    .get(`/user/settings`)
    .then((response) => {
      if (response.status === 200) {
        setMyUserSettings(response.data);
        if (response.data.theme) {
          setColorScheme(response.data.theme);
        }
      }
    })
    .catch(error => {
      // console.error('Error checking login status:', error);
    });
  }
  const updateMyUserSettings = (lang, username, avatarUrl) => {
    setMyUserSettings({
      'lang': lang,
      'userName': username,
      'avatarUrl': avatarUrl
    })
  }

  const handleLogout = () => {
    localStorage.removeItem('jwtToken');
    setLoginState('loggedOut');
    setMyUserInfo(null);
    setMyUserSettings(null);
    setUnreadCount(null);
  };

  const oauthLoginURLReceivedCallback = (url) => {
    setOauthLoginURL(url);
  }
  const handleLoginWithGoogle = () => {
    window.location.href = oauthLoginURL;
  };

  // Sidebar badge counts UNSEEN (FB/IG/VK semantics): opening the
  // notifications page clears it in bulk via POST /notifications/
  // markAllSeen, while per-item "unread" styling is independent and
  // only flips on explicit click.
  const fetchUnreadCount = () => {
    axiosInstance
    .get('/notifications/unseenCount')
    .then((response) => {
      setUnreadCount(response.data.unseenCount);
    })
    .catch(error => {
      // console.error('Failed to fetch unseen notification count:', error);
    })
  };

  // Google login.
  const [ user, setUser ] = useState(null);
  const login = useGoogleLogin({
      onSuccess: (codeResponse) => setUser(codeResponse),
      onError: (error) => console.log('Login Failed:', error)
  });
  useEffect(
    () => {
        if (user) {
            oauth.ProvideTokenToBackend({token: user.access_token, onJWTObtained: () => {
              // setJWTObtained(true);
              updateMyUserInfo();
              window.location.href = "/";
            }
            });
        }
    },
    [ user ]
  );

  const handleLoginIdentifierChange = (event) => {
    setEmailLogin(event.target.value);
    setLoginCodeRequested(false);
    setEmailLoginCode("");
    setEmailLoginMessage("");
    setEmailLoginError("");
  };

  const handleSendLoginCode = () => {
    const identifier = emailLogin.trim();
    if (!identifier) {
      setEmailLoginError(isRussianSpeaking ? "Введите email или имя пользователя" : "Enter email or username");
      return;
    }
    setEmailLoginError("");
    setEmailLoginMessage("");
    oauth.SendLoginCode({
      identifier,
      onResult: (data) => {
        if (!data.sent) {
          if (data.reason === "not_found") {
            setEmailLoginError(isRussianSpeaking ? "Пользователь не найден" : "User not found");
          } else if (data.reason === "invalid_email") {
            setEmailLoginError(isRussianSpeaking ? "Похоже, это не настоящий email" : "That doesn't look like a real email");
          } else if (data.reason === "rate_limited") {
            const secs = data.retryAfterSeconds || 60;
            setEmailLoginError(isRussianSpeaking
              ? `Слишком часто. Подождите ${secs} сек.`
              : `Too many attempts. Wait ${secs} s.`);
          } else {
            setEmailLoginError(isRussianSpeaking ? "Не удалось отправить код" : "Could not send code");
          }
          return;
        }
        setLoginCodeRequested(true);
        setEmailLoginMessage(
          isRussianSpeaking
            ? "Код отправлен в почту и в Telegram, если такой аккаунт существует"
            : "Code sent to email and Telegram, if such an account exists"
        );
      }
    }).catch(() => {
      setEmailLoginError(isRussianSpeaking ? "Не удалось отправить код" : "Could not send code");
    });
  };

  const handleLoginWithCode = (codeOverride) => {
    const identifier = emailLogin.trim();
    const code = (codeOverride || emailLoginCode).trim();
    if (!identifier || !code) {
      setEmailLoginError(isRussianSpeaking ? "Введите код" : "Enter code");
      return;
    }
    setEmailLoginError("");
    oauth.LoginWithCode({
      identifier,
      code,
      onJWTObtained: () => {
        updateMyUserInfo();
        window.location.href = "/";
      }
    }).catch(() => {
      setEmailLoginError(isRussianSpeaking ? "Код неверен или истёк" : "Code is wrong or expired");
    });
  };

  // Initialize Google Analytics
  useEffect(() => {
    ReactGA.initialize('G-SRJGEMSELK'); // Replace with your Google Analytics Measurement ID
    ReactGA.send({ hitType: "pageview", page: window.location.pathname + window.location.search });
  }, []);

  const [pageHeaderActive, setPageHeaderActive] = useState(false);
  const pageHeaderContextValue = useMemo(
    () => ({ active: pageHeaderActive, setActive: setPageHeaderActive }),
    [pageHeaderActive]
  );
  const [footerSuppressActive, setFooterSuppressActive] = useState(false);
  const footerSuppressContextValue = useMemo(
    () => ({ active: footerSuppressActive, setActive: setFooterSuppressActive }),
    [footerSuppressActive]
  );

  const mainPageContextValue = {
    selectedTab,
    setSelectedTab,
    repeatedEnter,
    setRepeatedEnter,
    myUserSettings,
    myUserInfo,
    unreadCount,
    isRussianSpeaking,
    emailLogin,
    emailLoginCode,
    emailLoginMessage,
    emailLoginError,
    loginCodeRequested,
    updateMyUserInfo,
    handleLoginIdentifierChange,
    setEmailLoginCode,
    handleSendLoginCode,
    handleLoginWithCode,
    handleLogout,
    updateMyUserSettings,
    setUnreadCount
  };

  const routerLoggedIn = useMemo(() => createBrowserRouter([
    {
      path: "/",
      element: <Layout loggedIn={true}/>,
      children: [
        {
          path: "/",
          element: <Navigate to={`/notifications`} replace />,
        },
        {
          path: "/addEvent",
          element: <AddEventWithContext />
        },
        {
          path: "/users/:pathUserId",
          element: <UserPage userId={null} />
        },
        {
          path: "/users/:pathUserId/likes",
          element: <PersonalArtEventsWithContext eventType="like" userId={null} />
        },
        {
          path: "/users/:pathUserId/recommendationsIncoming",
          element: <PersonalArtEventsWithContext eventType="recommendationIncoming" userId={null} />
        },
        {
          path: "/users/:pathUserId/recommendationsOutgoing",
          element: <PersonalArtEventsWithContext eventType="recommendationOutgoing" userId={null} />
        },
        {
          path: "/users/:pathUserId/recommendations",
          element: <PlatformRecommendations />
        },
        {
          path: "/topTodos",
          element: <TopTodos />
        },
        {
          path: "/users",
          element: <Users />
        },
        {
          path: "/settings",
          element: <SettingsWithContext />
        },
        {
          path: "/logbookHistory",
          element: <LogbookHistory />
        },
        {
          path: "/artEvents/:id",
          element: <ArtEvent eventType="artEvent"/>
        },
        {
          path: "/logbook",
          element: <LogbookPage />
        },
        {
          path: "/recommendations/:id",
          element: <ArtEvent eventType="recommendation"/>
        },
        {
          path: "/arts/:id",
          element: <ArtPage />
        },
        {
          path: "/arts/:id/review",
          element: <ArtReviewPage />
        },
        {
          path: "/reviews/:id",
          element: <ReviewByIdPage />
        },
        {
          path: "/notifications",
          element: <NotificationsWithContext />
        },
        {
          path: "/users/:pathUserId/recalc",
          element: <RecalcUser />
        },
        {
          path: "/artByLink",
          element: <ArtPage />
        },
        {
          path: "/tg-callback",
          element: <TgOauthCallback />
        },
        {
          path: "/createArt",
          element: <CreateArt />
        },
        {
          path: "/createArtProposal",
          element: <CreateArtProposal />
        },
        {
          path: "/artProposals",
          element: <ArtProposals />
        },
        {
          path: "/admin/letterboxdImportFailures",
          element: <AdminLetterboxdFailures />
        },
        {
          path: "/admin/searchNotFoundHints",
          element: <AdminSearchNotFoundHints />
        },
        {
          path: "/admin/directChatAllowedUsers",
          element: <AdminDirectChatAllowedUsers />
        },
        {
          path: "/admin/watchLinkAllowedUsers",
          element: <AdminWatchLinkAllowedUsers />
        },
        {
          path: "/directChats/:chatId",
          element: <DirectChatPage />
        },
        {
          path: "/groupChats/:chatId",
          element: <GroupChatPage />
        },
        {
          path: "/chats",
          element: <MyChats />
        }
      ]
    }
  ]), []);

  const routerLoggedOut = useMemo(() => {
    return createBrowserRouter([
      {
        path: "/",
        element: <Layout loggedIn={false}/>,
        children: [
          {
            path: "/",
            element: <LoggedOutHome />
          },
          {
            path: "/users",
            element: <Users />
          },
          {
            path: "/addEvent",
            element: <AddEvent />
          },
          {
            path: "/notifications",
            element: <Notifications />
          },
          {
            path: "/arts/:id",
            element: <ArtPage />
          },
          {
            path: "/arts/:id/review",
            element: <ArtReviewPage />
          },
          {
            path: "/reviews/:id",
            element: <ReviewByIdPage />
          },
          {
            path: "/users/:pathUserId/likes",
            element: <PersonalArtEventsWithContext eventType="like" userId={null} />
          },
          {
            path: "/logbookHistory",
            element: <LogbookHistory />
          },
          {
            path: "/artEvents/:id",
            element: <ArtEvent eventType="artEvent"/>
          },
          {
            path: "/newUser",
            element: <NewUser />
          },
          {
            path: "/artByLink",
            element: <ArtPage />
          },
          {
            path: "/tg-callback",
            element: <TgOauthCallback />
          }
        ]
      }
    ]);
  }, []);

  // // TODO: refactor, this block is ugly.
  // let isOauthRedirect = false;
  // const urlParams = new URLSearchParams(window.location.search);
  // const code = urlParams.get('code');
  // if (code !== null) {
  //   isOauthRedirect = true;
  // }

  // Sync Telegram theme with Mantine color scheme (only inside TG Mini App, only if user preference is "auto").
  useEffect(() => {
    const tgWebApp = window.Telegram?.WebApp;
    if (!tgWebApp || !tgWebApp.initData) return;
    const savedTheme = myUserSettings?.theme;
    if (savedTheme && savedTheme !== 'auto') return; // user has explicit preference, don't override
    if (tgWebApp.colorScheme) {
      setColorScheme(tgWebApp.colorScheme);
    }
    const onThemeChanged = () => {
      if (tgWebApp.colorScheme) {
        setColorScheme(tgWebApp.colorScheme);
      }
    };
    tgWebApp.onEvent('themeChanged', onThemeChanged);
    return () => tgWebApp.offEvent('themeChanged', onThemeChanged);
  }, [setColorScheme, myUserSettings]);

  // Telegram Web App auto-auth.
  const [tgAuthAttempted, setTgAuthAttempted] = useState(false);
  useEffect(() => {
    const tgWebApp = window.Telegram?.WebApp;
    if (tgWebApp && tgWebApp.initData && !tgAuthAttempted) {
      setTgAuthAttempted(true);
      tgWebApp.ready();
      tgWebApp.expand();
      oauth.TelegramWebAppAuth({
        initData: tgWebApp.initData,
        onJWTObtained: () => {
          updateMyUserInfo();
          fetchMyUserSettings();
          fetchUnreadCount();
        }
      }).catch(() => {
        // Fallback to normal auth check if TG auth fails.
        updateMyUserInfo();
        fetchMyUserSettings();
        fetchUnreadCount();
      });
      return;
    }
  }, [tgAuthAttempted]);

  useEffect(() => {
    if (tgAuthAttempted) return; // TG auth handles its own init.
    if (loginState === null && myUserSettings === null && unreadCount === null) {
      updateMyUserInfo();
      fetchMyUserSettings();
      fetchUnreadCount();
    }
  }, [loginState, myUserSettings, unreadCount, tgAuthAttempted]);

  // FEATURE: global AI badge on art thumbnails.
  // Fetches the user's AI-recommended art IDs once after login so any
  // ArtThumbnail can show the violet "AI" tag. Safe to delete with the rest
  // of the feature (MyUserContext field + ArtThumbnail badge block).
  useEffect(() => {
    if (loginState !== 'loggedIn') return;
    axiosInstance.get('/user/aiRecommendedArtIds')
      .then((res) => setAiRecommendedArtIds(new Set(res.data?.artIds || [])))
      .catch(() => {});
  }, [loginState]);

  // "Last seen" pulse: one POST per session load so we know when the user
  // last entered the web app. Fire-and-forget.
  useEffect(() => {
    if (loginState !== 'loggedIn') return;
    axiosInstance.post('/user/lastSeen').catch(() => {});
  }, [loginState]);

  if (!loginState) {
    return <div></div>
  }
  if (loginState === 'loggedIn' && !myUserSettings) { // if logged in, wait for settings
    return <div></div>
  }
  // loginStaet === 'loggedOut' -> myUserSettings are not needed

  // if (loginState === 'unknown' && !isOauthRedirect || (loginState === "loggedIn" && (myUserSettings === null || unreadCount === null))) {
  //   return <div></div>
  // }

  if (loginState === 'loggedOut') {
    return (
      <MainPageUIContext.Provider value={mainPageContextValue}>
        <RouterProvider router={routerLoggedOut}>
        </RouterProvider>
      </MainPageUIContext.Provider>
    );
  }

  return (
    <MainPageUIContext.Provider value={mainPageContextValue}>
      <MyUserContext.Provider value={{myUserInfo: myUserInfo, myUserSettings: myUserSettings, aiRecommendedArtIds: aiRecommendedArtIds}}>
        <PageHeaderContext.Provider value={pageHeaderContextValue}>
          <FooterSuppressContext.Provider value={footerSuppressContextValue}>
            <RouterProvider router={routerLoggedIn}>
            </RouterProvider>
          </FooterSuppressContext.Provider>
        </PageHeaderContext.Provider>
      </MyUserContext.Provider>
    </MainPageUIContext.Provider>
  );
  // TODO: Put in footer: Icons by <a target="_blank" href="https://icons8.com">Icons8</a>
}

export default MainPage;
